import { FormControl, FormControlLabel, Radio, RadioGroup as MuiRadioGroup } from '@mui/material';
import { useId } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';
import { useColors } from '../ThemeProvider';

type NonBoolean<T> = T extends boolean ? never : T;

interface Props<K> {
  id?: string;
  label: string;
  options: { value: K; label: string }[];
  value?: K;
  onChange: (value: K) => void;
  overrideVisited?: boolean;
  required?: boolean;
  errorText?: string;
}

export const RadioGroup = <R,>({
  id,
  label,
  options,
  value,
  onChange,
  overrideVisited = false,
  required = false,
  errorText = '',
}: Props<NonBoolean<R>>) => {
  const { primary } = useColors();

  const generatedId = useId();
  const radioGroupId = id ?? generatedId;
  const labelId = `${radioGroupId}-label`;
  const helperTextId = `${radioGroupId}-helper-text`;

  const showError = !!errorText && overrideVisited;

  return (
    <FormControl>
      <FormLabel
        componentId={radioGroupId}
        id={labelId}
        label={label}
        required={required}
        valid={!errorText}
      />

      <MuiRadioGroup
        id={radioGroupId}
        name={radioGroupId}
        aria-label={label}
        aria-labelledby={labelId}
        onChange={({ target: { value } }) => onChange(value as NonBoolean<R>)}
        value={value ?? ''}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.label}
            value={option.value}
            control={<Radio sx={{ color: primary }} />}
            label={option.label}
          />
        ))}
      </MuiRadioGroup>

      {showError && <FormHelperText id={helperTextId} showError={showError} text={errorText} />}
    </FormControl>
  );
};
